import React from 'react';
import { Trans } from '@lingui/macro';

import { Box, Flex, Link } from '@rover/kibble/core';
import Section from '@rover/kibble/patterns/Section';
import HowRoverWorks, { ImageOptions } from '@rover/react-lib/src/components/HowRoverWorks';
import LandingHero from '@rover/react-lib/src/pages/landing/components/LandingHero';
import Guarantees from '@rover/react-lib/src/pages/landing/ServiceLanding/components/Guarantees';
import HouseSittingServiceFaqs from '@rover/react-lib/src/pages/landing/ServiceLanding/components/ServiceFaqs/HouseSittingServiceFaqs';
import ServiceLandingServices from '@rover/react-lib/src/pages/landing/ServiceLanding/components/ServiceLandingServices';
import Props from '@rover/react-lib/src/pages/landing/ServiceLanding/pages/ServiceLandingPageProps';
import AppStoreCallout from '@rover/react-lib/src/pages/shared/AppStoreCallout/AppStoreCallout';
import CitiesAndNeighborhoods from '@rover/react-lib/src/pages/shared/CitiesAndNeighborhoods/CitiesAndNeighborhoods';
import {
  SERVICE_TYPE_SLUG_FOR_SERVICE_LANDING_ENUM,
  ServiceLandingEnum,
} from '@rover/types/src/ServiceLanding';

import LandingHelmet from '../../../components/LandingHelmet';
import { getSearchFiltersFromSearchResults } from '../../../utilities';
import ServiceLandingResultsLayout from '../../components/ServiceLandingResultsLayout';
import ServiceTargetAudience from '../../components/ServiceTargetAudience';
import ServiceValueProposition from '../../components/ServiceValueProposition';

const HouseSitting: React.FC<Props> = ({
  language,
  serviceLinks,
  countryConfig,
  citiesAndNeighborhoods,
  serviceLinksWithDisplayName,
  serviceLandingData,
  searchResults,
  frontendConfigSettings,
  adwordslocation,
  serviceTypes,
}) => {
  const serviceLandingType = ServiceLandingEnum.HOUSE_SITTING;
  const serviceTypeSlug = SERVICE_TYPE_SLUG_FOR_SERVICE_LANDING_ENUM[serviceLandingType];

  const { h1, h2, seoTitle, seoMetaDescription } = serviceLandingData;

  const initialSearchFilters = getSearchFiltersFromSearchResults(serviceLandingType, searchResults);

  return (
    <>
      <LandingHelmet title={seoTitle} metaDescription={seoMetaDescription} />
      <LandingHero mb="20x">
        <LandingHero.Content>
          <LandingHero.Display>{h1}</LandingHero.Display>
          <LandingHero.Text>{h2}</LandingHero.Text>
          <LandingHero.SearchForm
            language={language}
            serviceTypes={serviceTypes}
            serviceTypeSlug={serviceTypeSlug}
            initialSearchFilters={initialSearchFilters}
          />
          <LandingHero.TrustPilot languageCode={language} />
        </LandingHero.Content>
      </LandingHero>
      {searchResults && searchResults.results.length && (
        <ServiceLandingResultsLayout
          serviceLandingType={serviceLandingType}
          language={language}
          countryConfig={countryConfig}
          searchResults={searchResults}
          mapSettings={frontendConfigSettings}
          adwordslocation={adwordslocation}
        />
      )}
      <Section mt="0x" mb="40x">
        <Flex alignItems="center" flexDirection="column">
          <ServiceValueProposition>
            <ServiceValueProposition.Display>
              <Trans>Finally—a house sitting option that works</Trans>
            </ServiceValueProposition.Display>
            <ServiceValueProposition.List>
              <ServiceValueProposition.ListItem>
                <ServiceValueProposition.Subtitle>
                  <Trans>No need to uproot your pet</Trans>
                </ServiceValueProposition.Subtitle>
                <ServiceValueProposition.Text>
                  <Trans>
                    Book a house sitter to take care of your dog and cat, while keeping an eye on
                    your home. And the best part? Your best friend gets to stay on their home turf.
                  </Trans>
                </ServiceValueProposition.Text>
              </ServiceValueProposition.ListItem>

              <ServiceValueProposition.ListItem>
                <ServiceValueProposition.Subtitle>
                  <Trans>House sitters you can trust</Trans>
                </ServiceValueProposition.Subtitle>
                <ServiceValueProposition.Text>
                  <Trans>
                    Every house sitter profile has been individually reviewed and approved by Rover.
                  </Trans>
                </ServiceValueProposition.Text>
              </ServiceValueProposition.ListItem>

              <ServiceValueProposition.ListItem>
                <ServiceValueProposition.Subtitle>
                  <Trans>Easy Meet & Greets</Trans>
                </ServiceValueProposition.Subtitle>
                <ServiceValueProposition.Text>
                  <Trans>
                    Meet your pet’s perfect match before you book. Request a free in-person Meet &
                    Greet, right from your Rover inbox.
                  </Trans>
                </ServiceValueProposition.Text>
              </ServiceValueProposition.ListItem>

              <ServiceValueProposition.ListItem>
                <ServiceValueProposition.Subtitle>
                  <Trans>Cute photo updates</Trans>
                </ServiceValueProposition.Subtitle>
                <ServiceValueProposition.Text>
                  <Trans>
                    Millions of adorable photos have been sent through Rover! Get pics of your pet
                    enjoying a snuggle session with their favorite house sitter.
                  </Trans>
                </ServiceValueProposition.Text>
              </ServiceValueProposition.ListItem>
            </ServiceValueProposition.List>
          </ServiceValueProposition>
          <HowRoverWorks>
            <HowRoverWorks.Display>
              <Trans>Browse to book in 3 steps flat</Trans>
            </HowRoverWorks.Display>
            <HowRoverWorks.List>
              <HowRoverWorks.ListItem image={ImageOptions.MAGNIFYING_GLASS}>
                <HowRoverWorks.ItemHeading step={1}>
                  <Trans>Search Pet Sitters</Trans>
                </HowRoverWorks.ItemHeading>
                <HowRoverWorks.ItemContent>
                  <Trans>Browse profiles of nearby sitters who offer house sitting.</Trans>
                </HowRoverWorks.ItemContent>
              </HowRoverWorks.ListItem>

              <HowRoverWorks.ListItem image={ImageOptions.COLLAR}>
                <HowRoverWorks.ItemHeading step={2}>
                  <Trans>Meet In Person</Trans>
                </HowRoverWorks.ItemHeading>
                <HowRoverWorks.ItemContent>
                  <Trans>Schedule a free in-person Meet & Greet to find the perfect fit.</Trans>
                </HowRoverWorks.ItemContent>
              </HowRoverWorks.ListItem>

              <HowRoverWorks.ListItem image={ImageOptions.PHONE}>
                <HowRoverWorks.ItemHeading step={3}>
                  <Trans>Book and Pay</Trans>
                </HowRoverWorks.ItemHeading>
                <HowRoverWorks.ItemContent>
                  <Trans>
                    It’s that easy! All payments are processed through Rover's secured platform.
                  </Trans>
                </HowRoverWorks.ItemContent>
              </HowRoverWorks.ListItem>
            </HowRoverWorks.List>
          </HowRoverWorks>

          <Guarantees serviceTypeSlug={serviceTypeSlug} countryConfig={countryConfig} />

          <ServiceTargetAudience data-qa-id="service-landing-description">
            <ServiceTargetAudience.Display>
              <Trans>Book 5-star care that comes to you</Trans>
            </ServiceTargetAudience.Display>
            <ServiceTargetAudience.Content>
              <Trans>
                Heading out of town? It's easy to book a 5-star house sitter to take care of your
                pets and your home.
              </Trans>
            </ServiceTargetAudience.Content>
            <ServiceTargetAudience.List>
              <ServiceTargetAudience.ListItem>
                <Trans>Dogs who'd prefer to stay on their home turf</Trans>
              </ServiceTargetAudience.ListItem>
              <ServiceTargetAudience.ListItem>
                <Trans>
                  <Link
                    href={serviceLinks[ServiceLandingEnum.CAT_SITTING]}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Cats
                  </Link>{' '}
                  and caged pets
                </Trans>
              </ServiceTargetAudience.ListItem>
              <ServiceTargetAudience.ListItem>
                <Trans>Pet parents on a tight schedule</Trans>
              </ServiceTargetAudience.ListItem>
              <ServiceTargetAudience.ListItem>
                <Trans>Keeping an eye on your home while you're away</Trans>
              </ServiceTargetAudience.ListItem>
            </ServiceTargetAudience.List>
          </ServiceTargetAudience>

          <HouseSittingServiceFaqs />

          <ServiceLandingServices {...{ serviceLandingType, serviceLinksWithDisplayName }}>
            <ServiceLandingServices.Display>
              <Trans>Rover offers many services to meet your pet care needs, including:</Trans>
            </ServiceLandingServices.Display>
          </ServiceLandingServices>

          <CitiesAndNeighborhoods citiesOrNeighborhoods={citiesAndNeighborhoods}>
            <CitiesAndNeighborhoods.Display>
              <Trans>Thousands of house sitters in cities and neighborhoods, including:</Trans>
            </CitiesAndNeighborhoods.Display>
          </CitiesAndNeighborhoods>

          <Box my="8x" data-qa-id="service-landing-download-app-callout">
            <AppStoreCallout />
          </Box>
        </Flex>
      </Section>
    </>
  );
};

export default HouseSitting;
